import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem,
  Select, Switch, TextField,
} from '@mui/material';
import { listConnections, listSchemas, listTables } from '../../api/connections';

export const MONGO_MODULES = ['mongodb_query', 'mongodb_write'];
const MAX_LIMIT = 100000;

// Returns an error message, or '' when the text is empty or valid JSON of the expected kind.
export const validateJson = (text, kind) => {
  if (text === undefined || text === null) return '';
  if (typeof text === 'object') return '';
  if (!String(text).trim()) return '';
  let parsed;
  try { parsed = JSON.parse(text); } catch (e) { return `Invalid JSON: ${e.message}`; }
  if (kind === 'array') return Array.isArray(parsed) ? '' : 'Must be a JSON array';
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? '' : 'Must be a JSON object';
};

// Config values may be objects (saved by API/seeds) or text typed here.
const asText = (v) => (v && typeof v === 'object' ? JSON.stringify(v, null, 2) : v || '');

const JsonField = ({ label, value, kind, rows, hint, onChange }) => {
  const error = validateJson(value, kind);
  return (
    <TextField
      label={label} value={asText(value)} onChange={(e) => onChange(e.target.value)}
      fullWidth margin="dense" size="small" multiline minRows={rows} error={Boolean(error)}
      helperText={error || hint} InputProps={{ sx: { fontFamily: 'monospace' } }}
    />
  );
};

const MongoModuleConfig = ({ nodeId, moduleName, config = {}, onConfigChange }) => {
  const isWrite = moduleName === 'mongodb_write';
  const [connections, setConnections] = useState([]);
  const [listError, setListError] = useState(false);
  const [databases, setDatabases] = useState([]);
  const [collections, setCollections] = useState([]);
  const connectionId = config.connection_id || '';

  const set = (patch) => onConfigChange(nodeId, { ...config, ...patch });

  useEffect(() => {
    let cancelled = false;
    listConnections({ limit: 500 })
      .then((res) => {
        if (!cancelled) setConnections((res.connections || []).filter((c) => c.db_type === 'mongodb'));
      })
      .catch(() => { if (!cancelled) setListError(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!connectionId) { setDatabases([]); return undefined; }
    let cancelled = false;
    listSchemas(connectionId)
      .then((res) => { if (!cancelled) setDatabases(res.schemas || []); })
      .catch(() => { if (!cancelled) setDatabases([]); });
    return () => { cancelled = true; };
  }, [connectionId]);

  useEffect(() => {
    if (!connectionId || !config.database) { setCollections([]); return undefined; }
    let cancelled = false;
    listTables(connectionId, { schema: config.database })
      .then((res) => { if (!cancelled) setCollections((res.tables || []).map((t) => t.name)); })
      .catch(() => { if (!cancelled) setCollections([]); });
    return () => { cancelled = true; };
  }, [connectionId, config.database]);

  const mode = config.mode || (isWrite ? 'insert' : 'find');
  const keyFields = Array.isArray(config.key_fields) ? config.key_fields.join(', ') : config.key_fields || '';

  const freeSolo = (label, value, options, key, required) => (
    <Autocomplete
      freeSolo options={options} inputValue={value || ''}
      onInputChange={(_, v, reason) => { if (reason !== 'reset' || v) set({ [key]: v }); }}
      renderInput={(params) => (
        <TextField {...params} label={label} margin="dense" size="small" required={required} />
      )}
    />
  );

  return (
    <Box>
      {listError && <Alert severity="warning" sx={{ mb: 1 }}>Could not load saved connections.</Alert>}
      <FormControl fullWidth margin="dense" size="small">
        <InputLabel id={`mongo-conn-${nodeId}`}>Connection</InputLabel>
        <Select
          labelId={`mongo-conn-${nodeId}`} label="Connection" value={connectionId}
          onChange={(e) => {
            const patch = { connection_id: e.target.value || undefined };
            if (e.target.value) patch.uri = undefined;
            set(patch);
          }}
        >
          <MenuItem value="">None (use URI)</MenuItem>
          {connections.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </Select>
        <FormHelperText>Saved MongoDB connection, or enter a URI below</FormHelperText>
      </FormControl>
      {!connectionId && (
        <TextField
          label="URI" type="password" autoComplete="off" value={config.uri || ''}
          onChange={(e) => set({ uri: e.target.value })} fullWidth margin="dense" size="small"
          placeholder="mongodb+srv://user:pass@cluster0.xxxxx.mongodb.net/"
        />
      )}
      {freeSolo('Database', config.database, databases, 'database', true)}
      {freeSolo('Collection', config.collection, collections, 'collection', true)}

      <FormControl fullWidth margin="dense" size="small">
        <InputLabel id={`mongo-mode-${nodeId}`}>{isWrite ? 'Write mode' : 'Mode'}</InputLabel>
        <Select
          labelId={`mongo-mode-${nodeId}`} label={isWrite ? 'Write mode' : 'Mode'} value={mode}
          onChange={(e) => set({ mode: e.target.value })}
        >
          {(isWrite ? ['insert', 'upsert'] : ['find', 'aggregate']).map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {!isWrite && mode === 'find' && (
        <>
          <JsonField label="Filter (JSON)" kind="object" rows={3} value={config.filter}
                     hint='e.g. {"status": "active"}' onChange={(v) => set({ filter: v })} />
          <JsonField label="Projection (JSON)" kind="object" rows={2} value={config.projection}
                     hint='e.g. {"name": 1}' onChange={(v) => set({ projection: v })} />
          <JsonField label="Sort (JSON)" kind="object" rows={2} value={config.sort}
                     hint='e.g. {"created_at": -1}' onChange={(v) => set({ sort: v })} />
        </>
      )}
      {!isWrite && mode === 'aggregate' && (
        <JsonField label="Pipeline (JSON array)" kind="array" rows={6} value={config.pipeline}
                   hint="Aggregation stages. $out and $merge are not allowed."
                   onChange={(v) => set({ pipeline: v })} />
      )}
      {!isWrite && (
        <TextField
          label="Limit" type="number" value={config.limit ?? 1000} fullWidth margin="dense" size="small"
          inputProps={{ min: 1, max: MAX_LIMIT }}
          onChange={(e) => set({ limit: e.target.value === '' ? undefined : Number(e.target.value) })}
          helperText={`1 – ${MAX_LIMIT.toLocaleString()} documents`}
        />
      )}

      {isWrite && (
        <>
          <TextField
            label="Key fields" value={keyFields} fullWidth margin="dense" size="small"
            onChange={(e) => set({ key_fields: e.target.value.split(',').map((k) => k.trim()).filter(Boolean) })}
            error={mode === 'upsert' && !keyFields}
            helperText={mode === 'upsert' && !keyFields
              ? 'Required for upsert' : 'Comma-separated top-level fields that identify a document'}
          />
          <TextField
            label="Batch size" type="number" value={config.batch_size ?? 1000} fullWidth margin="dense" size="small"
            onChange={(e) => set({ batch_size: e.target.value === '' ? undefined : Number(e.target.value) })}
          />
          <FormControlLabel
            control={<Switch checked={Boolean(config.keep_id)} onChange={(e) => set({ keep_id: e.target.checked })} />}
            label="Keep _id"
          />
          <FormHelperText>When off, _id is dropped so MongoDB assigns its own</FormHelperText>
        </>
      )}
    </Box>
  );
};

export default MongoModuleConfig;
